import React from 'react';
import { connect, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';

import { AppState } from '../../../store';
import BulkAcceptAwardsForm from '../forms/BulkAcceptAwardsForm';
import { BulkAcceptAwardsObject } from '../../interfaces/Opportunities';
import { closeMobilePageDetailOverlay } from 'src/core/ducks/mobilePage';
import { currentUserId, currentVendorId } from '../../../vendors/services/currentVendorSelector';
import { createSuccessNotification, createErrorNotification } from '../../../core/services/createNotification';
import { EndDestination } from '../../interfaces/ChangeRequests';
import { DuckFunction } from '../../../contracts/ducks';
import { getQueryParams } from '../../../utils/services/queryParams';
import { Grid, GridColumn, ModalTitle, ModalSubtitle } from '../../../core/components/styled';
import { loadDispatchOpportunitiesCount } from '../../../common/ducks';
import { loadOpportunities, updateBulkAwards } from '../../ducks/opportunities';
import { Modal } from '../../../core/components';
import translate from '../../../core/services/translate';
import { useIsMobileView } from 'src/utils/hooks';
import { YES } from '../../constants/status';

interface ComponentProps extends RouteComponentProps {
  endDestinations: EndDestination[];
  selectedOpenAwardsItemIds: number[];
  onCancel(pristine: boolean, successful?: boolean): void;
}
interface ReduxProps {
  closeMobilePageDetailOverlay: DuckFunction<typeof closeMobilePageDetailOverlay>;
  loadDispatchOpportunitiesCount: DuckFunction<typeof loadDispatchOpportunitiesCount>;
  loadOpportunities: DuckFunction<typeof loadOpportunities>;
  updateBulkAwards: DuckFunction<typeof updateBulkAwards>;
}

type Props = ComponentProps & ReduxProps;

const BulkAcceptAwardsModal: React.FC<Props> = props => {
  const vendorId = useSelector(currentVendorId);
  const userId = useSelector(currentUserId);
  const paymentInfo = useSelector((state: AppState) => state.opportunity.opportunities.paymentInfo);

  const { endDestinations, onCancel } = props;

  const onSubmit = (formData: any) => {
    const {
      location: { search },
      selectedOpenAwardsItemIds,
      loadDispatchOpportunitiesCount,
      loadOpportunities,
      updateBulkAwards,
      onCancel,
      closeMobilePageDetailOverlay,
    } = props;

    const { showActionRequired, startDate, endDate } = getQueryParams(search);
    const isActionRequired = showActionRequired === YES;
    formData.facilityId = !formData.facilityId ? '0' : formData.facilityId;

    let bulkAwardsObj: BulkAcceptAwardsObject = {};
    bulkAwardsObj.itemIds = selectedOpenAwardsItemIds;
    bulkAwardsObj.Response = YES;
    bulkAwardsObj.respondedYes = formData;

    const promise = updateBulkAwards(bulkAwardsObj, userId, vendorId, isActionRequired, startDate, endDate);

    if (!promise) {
      return;
    }

    promise
      .then(() => {
        loadDispatchOpportunitiesCount(userId, vendorId, '');
        loadOpportunities(userId, vendorId, isActionRequired, startDate, endDate);
        createSuccessNotification(translate('opportunity.opportunityBulkSave'));
        onCancel(true, true);
        closeMobilePageDetailOverlay();
      })
      .catch(() => {
        createErrorNotification(translate('opportunity.opportunitySaveError'));
      });
  };

  const isMobile = useIsMobileView();

  const BulkAcceptAwardsFormInitialValues = {
    paymentMethodType: paymentInfo?.paymentMethods[0].value,
    paymentId: paymentInfo?.vendorPayments[0].paymentId,
  };

  return (
    <Modal size="small">
      <ModalTitle>{translate('opportunity.acceptService')}</ModalTitle>
      <ModalSubtitle />
      <Grid centered>
        <GridColumn size={isMobile ? '12/12' : '10/12'}>
          <BulkAcceptAwardsForm
            initialValues={BulkAcceptAwardsFormInitialValues}
            endDestinations={endDestinations}
            onSubmit={onSubmit}
            onCancel={onCancel}
          />
        </GridColumn>
      </Grid>
    </Modal>
  );
};

const mapStateToProps = (state: AppState) => ({
  isSaving: state.opportunity.opportunities.isSaving,
});

const mapDispatchToProps = {
  closeMobilePageDetailOverlay,
  loadOpportunities,
  loadDispatchOpportunitiesCount,
  updateBulkAwards,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BulkAcceptAwardsModal));
